import { defineStore } from 'pinia'

import { ENOVIA_GROUP_STATUS_NAME } from '~/config/tagger-or-enovia'

import type { COMMUNITY_ID } from '~/entities/container/community/interfaces/community'
import type { UpdateIdeaStatusOptions } from '~/entities/content/idea/api/ideas'
import { IdeasService, updateIdeaStatus } from '~/entities/content/idea/api/ideas'
import { DS6WStatus } from '~/entities/content/idea/interfaces/exalead-idea'

import SwymServices from '~/features-shared/content/api/contents'
import ExaleadServices from '~/features-shared/content/api/exalead-contents'
import { IContentModel } from '~/features-shared/content/interfaces/content-model'
import { mergeContent } from '~/features-shared/content/stores/contents-db'
import { useRefineGlobalContentsStore as useRefineGContentsStore } from '~/features-shared/content-feed/composables/refine-contents'
import { ServiceFetchControllerDefault } from '~/features-shared/content-feed/controllers/services-fetch-controller-default'

import type { IExaleadGroup } from '~/features-generic/services/interfaces/exalead-groups'

// Interfaces

interface Status {
  color: string
  count: number
  ds6w: string
  position: number
  title: string
}

type Statuses = Status[]

const IDEA_EXALEAD_STATUS_GROUP_NAME = ENOVIA_GROUP_STATUS_NAME

export function useGenericRefineIdeas(controller = new ServiceFetchControllerDefault(SwymServices[IContentModel.Idea], ExaleadServices[IContentModel.Idea])) {
  const store = useRefineGContentsStore(controller)
  const states = {
    statusesList: ref<Record<string, { position: number, color: string }>>(),
    statuses: ref<Map<COMMUNITY_ID, Statuses>>(new Map()),
  }
  const currentGroups = computed(() => store.getGroups(store.context.value))

  const extractMaturitiesFromGroups = (groups: IExaleadGroup[]) => {
    const status = groups && groups.find(g => g.id === IDEA_EXALEAD_STATUS_GROUP_NAME)
    const category = status && status.categories && status.categories.length && status.categories[0]
    const subCategories = category && category.categories

    const extractedMaturities = subCategories && subCategories
      .map(c => ({
        ds6w: `f/${IDEA_EXALEAD_STATUS_GROUP_NAME}/xsd:string/${c.title}`,
        title: c.title.replace(/&#47;/g, '/'),
        count: c.count || 0,
      }))

    return extractedMaturities || []
  }

  watch([currentGroups, store.context, states.statusesList], async ([groups, context, statusesData]) => {
    if (!context)
      return

    if (!statusesData)
      return

    const extractedMaturities = extractMaturitiesFromGroups(groups)
    const statuses = Object.entries(statusesData)
      .map(([title, el]) => (
        {
          title,
          count: 0,
          ...el,
          // eslint-disable-next-line max-nested-callbacks
          ...extractedMaturities.find(e => e.title === title),
        } as Status))
    statuses.sort((a, b) => a.position - b.position)

    states.statuses.value.set(context, statuses)
  })

  const computations = {
    statuses: computed(() => {
      const context = store.context.value
      const statuses = context && states.statuses.value.get(context)
      return statuses || []
    }),
  }

  const actions = {
    fetchIdeaStatuses: async (options: { communityUri: string, derivedTypeId?: string }) => {
      const statusesData = await IdeasService.getStatuses({
        communityUri: options.communityUri,
        derivedTypeId: options.derivedTypeId,
      })

      // Get Positions
      // Hack because APIs does not have same data
      statusesData[DS6WStatus.Out] = { position: Number.MAX_VALUE, color: '#d8d8d8' }

      states.statusesList.value = statusesData
    },
    updateIdeaStatus: async (options: UpdateIdeaStatusOptions) => {
      mergeContent((await updateIdeaStatus(options)).result)
    },
  }

  const methods = {
    clear() {
      const context = store.context.value
      if (context)
        states.statuses.value.set(context, [])

      store.doClear()
    },
  }

  return {
    // States
    statuses: computations.statuses,
    limit: store.limit,
    groups: store.groups,

    // Tags
    tags: store.tags,
    tagsWithSubjectURIData: store.tagsWithSubjectURIData,
    tagsWithoutSubjectURIData: store.tagsWithoutSubjectURIData,

    // Computed
    total: store.total,
    currentContents: store.currentContents,

    // Getters
    getContents: store.getContents,

    // Actions
    doFetchNext: store.doFetchNext,
    doFetchMeta: store.doFetchMeta,
    doFetchIdeasStatuses: actions.fetchIdeaStatuses,
    doUpdateIdeaStatus: actions.updateIdeaStatus,
    doInsert: store.doInsert,
    doClear: methods.clear,
    doClearGroups: store.doClearGroups,
  }
}

export const useRefineIdeasStore = defineStore('refineIdeas', () => useGenericRefineIdeas())
